import { useMemo } from 'react';
import { X, Lock, Gavel } from 'lucide-react';
import { ModalRoot } from '@/components/ui/ModalRoot';
import { Z_MODALS } from '@/lib/zIndex';
import { useGameStore } from '@/state/gameStore';
import { effectiveHoldingCells } from '@/data/stations';
import { formatClock, formatEuro } from '@/lib/format';

interface HeldPersonsModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Phase 10.5.1 — Festgenommene-Liste.
 *
 * Listet alle Personen in Wachzellen über alle Polizei-Wachen hinweg. Pro
 * Person: Name, Wache, "seit"-Zeit, Cell-Auslastung. Button „Ins Gericht
 * überstellen" = +5 000 € + freier Cell-Slot.
 */
export function HeldPersonsModal({ open, onClose }: HeldPersonsModalProps) {
  const stations = useGameStore((s) => s.stations);
  const transferHeldToCourt = useGameStore((s) => s.transferHeldToCourt);
  const gameTime = useGameStore((s) => s.gameTime);

  const policeStations = useMemo(
    () =>
      stations.filter(
        (s) =>
          s.kind === 'police-station' ||
          s.kind === 'riot-police-station' ||
          s.kind === 'sek-base' ||
          s.kind === 'federal-police-station',
      ),
    [stations],
  );

  if (!open) return null;

  const flatHeld = policeStations
    .flatMap((s) =>
      (s.heldPersons ?? []).map((p) => ({
        ...p,
        stationId: s.id,
        stationName: s.name,
        stationCapacity: effectiveHoldingCells(s),
      })),
    )
    .sort((a, b) => a.arrestedAt - b.arrestedAt);

  return (
    <ModalRoot
      onClose={onClose}
      zIndex={Z_MODALS}
      contentClass="w-full max-w-2xl max-h-[85vh] overflow-hidden rounded-lg border border-border-strong bg-bg-secondary"
    >
      <header className="flex items-start justify-between bg-blue-700 px-5 py-4 text-white">
        <div>
          <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest opacity-80">
            <Lock className="h-3 w-3" aria-hidden /> Wachzellen / Justiz
          </p>
          <h3 className="font-display text-xl font-bold">Festgenommene ({flatHeld.length})</h3>
          <p className="mt-0.5 text-[11px] opacity-80">
            Pro Überstellung: +{formatEuro(5_000)} · Auto-Überstellung nach 48 h Spielzeit.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Schließen"
          className="flex h-8 w-8 items-center justify-center rounded-md bg-white/15 transition hover:bg-white/25"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </header>

      <div className="max-h-[60vh] overflow-y-auto">
        {flatHeld.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-fg-muted">
            Keine Festgenommenen aktuell in Verwahrung.
          </p>
        ) : (
          <ul className="divide-y divide-border-subtle">
            {flatHeld.map((p) => {
              const sinceMs = gameTime - p.arrestedAt;
              const minutes = Math.max(0, Math.round(sinceMs / 60_000));
              return (
                <li
                  key={p.id}
                  className="flex items-center gap-3 px-5 py-3"
                >
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-blue-500/15 text-blue-400">
                    <Lock className="h-4 w-4" aria-hidden />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-fg-primary">{p.name}</p>
                    <p className="text-[11px] text-fg-muted">
                      {p.stationName} · seit {formatClock(p.arrestedAt)} ({minutes} min)
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => transferHeldToCourt(p.stationId, p.id)}
                    className="flex items-center gap-1.5 rounded-md bg-accent-dispatch px-3 py-1.5 text-[11px] font-semibold text-white transition hover:bg-accent-dispatch-hover"
                  >
                    <Gavel className="h-3 w-3" aria-hidden /> Überstellen +{formatEuro(5_000)}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {policeStations.length > 0 && (
        <div className="border-t border-border-subtle bg-bg-secondary px-5 py-3 text-[11px] text-fg-muted">
          Wachen-Auslastung:{' '}
          {policeStations
            .map(
              (s) =>
                `${s.name}: ${(s.heldPersons ?? []).length}/${effectiveHoldingCells(s)}`,
            )
            .join(' · ')}
        </div>
      )}
    </ModalRoot>
  );
}
